import React, { useState } from "react";
import { graphql, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Label } from "src/components/ui/label";
import { SecretInput } from "src/components/ui/secret-input";
import { ErrorAlert, SuccessAlert } from "src/components/ui/status-alert";
import { Tooltip } from "src/components/ui/tooltip";
import { usePermissions } from "src/context/MeContext";

import { CursorIntegrationFormUpdateIntegrationMutation } from "./__generated__/CursorIntegrationFormUpdateIntegrationMutation.graphql";
import { CursorIntegrationFormValidateIntegrationMutation } from "./__generated__/CursorIntegrationFormValidateIntegrationMutation.graphql";
import { IntegrationType } from "./__generated__/IntegrationsSection_organization.graphql";

const updateIntegrationMutation = graphql`
  mutation CursorIntegrationFormUpdateIntegrationMutation(
    $input: UpdateIntegrationInput!
  ) {
    updateIntegration(input: $input) {
      integration {
        id
        name
        integrationType
        credentials
      }
      error
    }
  }
`;

const validateIntegrationMutation = graphql`
  mutation CursorIntegrationFormValidateIntegrationMutation(
    $input: ValidateIntegrationInput!
  ) {
    validateIntegration(input: $input) {
      success
      error
    }
  }
`;

interface CursorIntegrationFormProps {
  onComplete: (success: boolean) => void;
  onCancel: () => void;
}

export default function CursorIntegrationForm({ onComplete, onCancel }: CursorIntegrationFormProps) {
  const [apiKey, setApiKey] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [validationSuccess, setValidationSuccess] = useState<string | null>(null);
  const [isValidated, setIsValidated] = useState(false);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");

  const [commitMutation, isMutationInFlight] = useMutation<CursorIntegrationFormUpdateIntegrationMutation>(
    updateIntegrationMutation,
  );
  const [commitValidateMutation, isValidationInFlight] = useMutation<CursorIntegrationFormValidateIntegrationMutation>(
    validateIntegrationMutation,
  );

  const handleTest = () => {
    setFormError(null);
    setValidationSuccess(null);
    setIsValidated(false);

    if (!apiKey.trim()) {
      setFormError("API Key is required");
      return;
    }

    const credentials = { api_key: apiKey.trim() };
    const input = {
      integrationType: "CURSOR" as IntegrationType,
      credentials: JSON.stringify(credentials),
    };

    commitValidateMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          setFormError(errors.map((e) => e.message).join(", "));
          return;
        }
        if (!response.validateIntegration.success) {
          setFormError(response.validateIntegration.error || "Validation failed");
          return;
        }
        setValidationSuccess("Credentials validated successfully");
        setIsValidated(true);
      },
      onError: (error) => setFormError(error.message),
    });
  };

  const handleSave = () => {
    setFormError(null);
    setValidationSuccess(null);

    if (!apiKey.trim()) {
      setFormError("API Key is required");
      return;
    }

    const credentials = { api_key: apiKey.trim() };
    const input = {
      integrationType: "CURSOR" as IntegrationType,
      credentials: JSON.stringify(credentials),
    };

    commitMutation({
      variables: { input },
      onCompleted: (response, errors) => {
        if (errors && errors.length > 0) {
          setFormError(errors.map((e) => e.message).join(", "));
          onComplete(false);
          return;
        }
        if (response.updateIntegration.error) {
          setFormError(response.updateIntegration.error);
          onComplete(false);
          return;
        }
        onComplete(true);
      },
      onError: (error) => {
        setFormError(error.message);
        onComplete(false);
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="border-t border-border pt-4">
        <h3 className="text-base font-semibold text-foreground mb-4">API Key</h3>

        <div className="space-y-2">
          <Label htmlFor="cursor-api-key" className="text-muted-foreground">
            API Key
          </Label>
          <Tooltip
            content={!canModify ? "You don't have permission to modify integrations" : null}
            position="top"
            asChild
          >
            <span className="block w-full">
              <SecretInput
                id="cursor-api-key"
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  setIsValidated(false);
                  setValidationSuccess(null);
                }}
                placeholder="key_..."
                disabled={!canModify}
              />
            </span>
          </Tooltip>
        </div>
      </div>

      {formError && <ErrorAlert icon="none">{formError}</ErrorAlert>}
      {validationSuccess && <SuccessAlert icon="none">{validationSuccess}</SuccessAlert>}

      <div className="flex justify-end space-x-3 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={isMutationInFlight || isValidationInFlight}>
          Cancel
        </Button>
        <Tooltip
          content={!canModify ? "You don't have permission to connect integrations" : null}
          position="top"
        >
          <span className="inline-block">
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={isMutationInFlight || isValidationInFlight || !canModify}
            >
              {isValidationInFlight ? "Testing..." : "Test Connection"}
            </Button>
          </span>
        </Tooltip>
        <div className="relative group">
          <Tooltip
            content={!canModify ? "You don't have permission to connect integrations" : null}
            position="top"
          >
            <span className="inline-block">
              <Button
                onClick={handleSave}
                disabled={!isValidated || isMutationInFlight || isValidationInFlight || !canModify}
              >
                {isMutationInFlight ? "Saving..." : "Connect"}
              </Button>
            </span>
          </Tooltip>
          {!isValidated && !isMutationInFlight && !isValidationInFlight && canModify && (
            <div className="absolute bottom-full mb-2 right-0 px-3 py-2 bg-card text-foreground text-sm rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap">
              Test the connection first.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
